/**
 * Google Analytics 4 (gtag.js) 헬퍼.
 * - gtag 로더와 초기 config(첫 page_view)는 index.html <head>에서 처리한다.
 * - 여기서는 SPA 라우트 전환 시 수동 page_view 이벤트만 전송한다.
 * - SSR/프리렌더 환경에서는 window/gtag가 없으므로 안전하게 no-op 처리한다.
 */

type GtagFn = (...args: unknown[]) => void

declare global {
    interface Window {
        gtag?: GtagFn
        dataLayer?: unknown[]
    }
}

/** 클라이언트에서 gtag 사용 가능 여부 */
const isGtagReady = (): boolean =>
    typeof window !== 'undefined' && typeof window.gtag === 'function'

/**
 * SPA 라우트 전환 시 수동 page_view 전송.
 * 초기 진입 page_view는 index.html의 gtag('config', ...)가 이미 보낸다.
 */
export function trackPageview(path: string): void {
    if (!isGtagReady()) return
    window.gtag?.('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
    })
}

/**
 * 커스텀 이벤트 전송(예: 예약 버튼 클릭, 전화 클릭 등).
 * 필요할 때 컴포넌트에서 호출한다.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
    if (!isGtagReady()) return
    window.gtag?.('event', name, params)
}

/** CTA 종류 — GA4에서 cta_type 커스텀 측정기준으로 등록해 퍼널/세분화에 사용 */
export type CtaType =
    | 'reserve' // 네이버 예약 (핵심 전환)
    | 'call' // 전화 걸기
    | 'map' // 지도/위치 보기
    | 'directions' // 길찾기
    | 'reviews' // 리뷰 보기
    | 'instagram' // 인스타그램
    | 'more_photos' // 사진 더보기
    | 'naver_info' // 네이버 플레이스 정보
    | 'email' // 이메일

/** CTA가 노출된 위치 — GA4에서 location 커스텀 측정기준으로 등록 */
export type CtaLocation = 'hero' | 'gallery' | 'sticky_cta' | 'footer' | 'story' | 'local_intent' | 'header' | 'location_page'

/**
 * CTA 클릭 퍼널 이벤트.
 * 단일 이벤트명(cta_click) + 파라미터(cta_type, location)로 통일해
 * GA4 탐색(Exploration)에서 page_view → cta_click(reserve) 퍼널과
 * 위치별(hero/gallery/sticky/footer) 전환 기여를 분석할 수 있게 한다.
 */
export function trackCtaClick(ctaType: CtaType, location: CtaLocation): void {
    trackEvent('cta_click', { cta_type: ctaType, location })
}
